import React, { useMemo } from 'react';
import styled from 'styled-components';
import { navigate } from 'hooks/useHookRouterCompat';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Color, FontSize, FontWeight } from 'lib/styles';
import Button from 'components/UI/Button';
import DocumentCardGroup from 'components/Consignments/DocumentCardGroup';
import { Text } from 'styles/themeColors';
import Icon from 'view/Icon';
import { TEXT_LABELS } from 'constants/order';
import { classifyDocumentGroups, DocumentGroup } from 'utils/document';

const Heading = styled.h3`
  font-size: ${FontSize.large};
  line-height: 28px;
  margin-bottom: 12px;
  font-weight: ${FontWeight.bold};
`;

const LabelText = styled.span`
  display: block;
  text-align: left;
  margin: 12px 0;
  color: ${Text.primary};
  font-size: 16px;
  font-weight: bold;
`;

interface Props {
  consignmentId?: string;
  vendorId: string;
  documents?: any;
  orderDocuments: any;
  customerEmail: string;
  orderNumber: string;
}

const Documents = ({
  consignmentId,
  vendorId,
  documents,
  orderDocuments,
  customerEmail,
  orderNumber,
}: Props) => {
  const { fromCustomer, forCustomer } = useMemo(
    () => classifyDocumentGroups(orderDocuments, documents),
    [orderDocuments, documents]
  );

  if (fromCustomer.length === 0 && forCustomer.length === 0) return null;

  // A vendor group is addressed to the customer, so it gets the date column and
  // the "Email to customer" action; a customer's own upload does not.
  // `comment` stays a string rather than undefined, so a null comment is still
  // sent as an empty value in the email-to-customer payload instead of dropped.
  const renderGroups = (groups: DocumentGroup[], bucket: string, cardForCustomer?: boolean) =>
    groups.map((group, index) => (
      <DocumentCardGroup
        key={`${bucket}-${group.id ?? index}`}
        cardForCustomer={cardForCustomer}
        cards={group.documents}
        createdBy={group.createdBy ?? undefined}
        comment={group.comment ?? ''}
        date={group.creationTime ?? undefined}
        customerEmail={customerEmail}
        orderNumber={orderNumber}
      />
    ));

  return (
    <FlexboxContainer
      flexDirection="column"
      borderRadius="6px"
      borderColor={Color.lightGray}
      padding="16px"
      bgColor={Color.lightGray}
      width="100%"
    >
      <Heading id="documents">{TEXT_LABELS.DOCUMENTS}</Heading>
      {fromCustomer.length > 0 && (
        <>
          <LabelText>{TEXT_LABELS.DOCUMENTS_FROM_CUSTOMER}</LabelText>
          {renderGroups(fromCustomer, 'from')}
        </>
      )}
      {forCustomer.length > 0 && (
        <>
          <LabelText>{TEXT_LABELS.DOCUMENTS_FOR_CUSTOMER}</LabelText>
          {renderGroups(forCustomer, 'for', true)}
        </>
      )}
      <Button
        margin='0 0 0 12px'
        onClick={() => navigate(`/orders/order-details/${consignmentId}/new-document`, false, { vendorId })}
        theme='disabled'
        color='#666C73'
        borderColor='#c1c9d1'
        bgColor={Color.lightGray}
        leftIcon={() => <Icon name="FileUploadV2" size={17} />}
      >
        {TEXT_LABELS.ATTACH_DOCUMENTS}
      </Button>
    </FlexboxContainer>
  );
};

export default React.memo(Documents);
